import React, { useCallback, useState } from 'react';
import { InstagramIcon, PlayIcon, ExpandIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { SectionHeading } from './SectionHeading';
import { BeforeAfter } from './BeforeAfter';
import { MediaLightbox } from './MediaLightbox';
import { transformations, actionMedia, ActionMedia } from '../data/gallery';
import { business } from '../data/business';
type Tab = 'before-after' | 'in-action';
export function Gallery() {
  const {
    t,
    lang
  } = useLanguage();
  const [tab, setTab] = useState<Tab>('before-after');
  const [active, setActive] = useState<ActionMedia | null>(null);
  const close = useCallback(() => setActive(null), []);
  const tabs: {
    id: Tab;
    label: string;
  }[] = [{
    id: 'before-after',
    label: t.gallery.tabs.beforeAfter
  }, {
    id: 'in-action',
    label: t.gallery.tabs.inAction
  }];
  return <section id="gallery" className="bg-white py-20 sm:py-24" data-id="element-175" data-mp="21xzkgf4r1c1w">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8" data-id="element-176" data-mp="21xpq6fco8iqe">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between" data-id="element-177" data-mp="rdcjugi45lzz">
          <SectionHeading kicker={t.gallery.kicker} title={t.gallery.title} subtitle={t.gallery.subtitle} data-id="element-178" data-mp="waum5o1ml2z0" />
          <a href={business.instagramHref} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 self-start text-sm font-semibold text-brand-blue transition-colors duration-150 hover:text-navy lg:self-auto" data-id="element-179" data-mp="114h0dknftewx">
            <InstagramIcon className="h-4 w-4" aria-hidden="true" data-id="element-180" data-mp="dd1trfomobfv" />
            {t.gallery.more}
          </a>
        </div>

        <div className="mt-8 inline-flex rounded-lg bg-mist p-1" role="tablist" aria-label={t.gallery.title} data-id="element-181" data-mp="mdgi6nww26nl">
          {tabs.map(item => {
          const selected = tab === item.id;
          return <button key={item.id} type="button" role="tab" id={`tab-${item.id}`} aria-selected={selected} aria-controls={`panel-${item.id}`} onClick={() => setTab(item.id)} className={`rounded-md px-4 py-2 text-sm font-semibold transition-colors duration-150 sm:px-5 ${selected ? 'bg-navy text-white shadow-card' : 'text-ink/70 hover:text-navy'}`} data-id="element-182" data-mp="1xrbwjufyybdr">
                {item.label}
              </button>;
        })}
        </div>

        {tab === 'before-after' ? <div id="panel-before-after" role="tabpanel" aria-labelledby="tab-before-after" className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-id="element-183" data-mp="3kutl0jupbdsm">
            {transformations.map(item => <figure key={item.id} className="flex flex-col" data-id="element-184" data-mp="2sd7ombpn8jrq">
                <BeforeAfter before={item.before} after={item.after} alt={item.alt[lang]} beforeLabel={t.gallery.before} afterLabel={t.gallery.after} sliderLabel={t.gallery.sliderLabel} hint={t.gallery.hint} data-id="element-185" data-mp="2nghbnx94y4aj" />
                <figcaption className="mt-3" data-id="element-186" data-mp="tuvzip910w3p">
                  <span className="block font-display text-base font-bold text-navy" data-id="element-187" data-mp="xxsmst9ihgdz">
                    {item.title[lang]}
                  </span>
                  <span className="mt-0.5 block text-xs font-medium text-ink/60" data-id="element-188" data-mp="1lm1z94ku4rsg">
                    {item.vehicle[lang]}
                  </span>
                </figcaption>
              </figure>)}
          </div> : <div id="panel-in-action" role="tabpanel" aria-labelledby="tab-in-action" className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-id="element-189" data-mp="1hm1l7l1neilx">
            {actionMedia.map(item => <figure key={item.id} className="flex flex-col" data-id="element-190" data-mp="68pi5cdpq40t">
                <button type="button" onClick={() => setActive(item)} aria-label={`${item.type === 'video' ? t.gallery.play : t.gallery.view}: ${item.title[lang]}`} className="group relative block aspect-[4/5] w-full overflow-hidden rounded-lg bg-navy" data-id="element-191" data-mp="li2xe10obpt9">
                  <img src={item.poster} alt="" loading="lazy" className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]" data-id="element-192" data-mp="14gf5rkjrgvns" />
                  <span className="absolute inset-0 bg-navy/20 transition-colors duration-200 group-hover:bg-navy/35" data-id="element-193" data-mp="vhbmogklgg9a" />
                  <span className="absolute left-1/2 top-1/2 flex h-14 w-14 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white/95 text-navy shadow-lift transition-transform duration-200 group-hover:scale-105" data-id="element-194" data-mp="3g5g3gtzry0zd">
                    {item.type === 'video' ? <PlayIcon className="ml-0.5 h-6 w-6 fill-navy" aria-hidden="true" data-id="element-195" data-mp="1vnf4278qdlux" /> : <ExpandIcon className="h-5 w-5" aria-hidden="true" data-id="element-196" data-mp="1zp8y3wof7rm4" />}
                  </span>
                </button>
                <figcaption className="mt-3" data-id="element-197" data-mp="24ha81by4968z">
                  <span className="block font-display text-base font-bold text-navy" data-id="element-198" data-mp="29f2c14c1citj">
                    {item.title[lang]}
                  </span>
                  <span className="mt-0.5 block text-sm text-ink/60" data-id="element-199" data-mp="2ecbx67se68bk">
                    {item.description[lang]}
                  </span>
                </figcaption>
              </figure>)}
          </div>}
      </div>

      <MediaLightbox item={active} lang={lang} closeLabel={t.gallery.close} onClose={close} data-id="element-200" data-mp="oyyrae4msc8c" />
    </section>;
}